import { useState } from 'react'
import { signIn } from '../auth'
import driveLogoUrl from '../assets/google-drive-logo.jpg'

/* SignInGuide is reachable by anyone with the URL, signed in or not — Firebase
   Hosting is unauthenticated (the same rule that keeps the catalogue off it,
   HANDOVER.md §7). It therefore shows only the brand and the sign-in action,
   nothing about the structure. */

/* General, true claims about the portal — never a folder, drive, or branch
   name. This is the only copy an unauthenticated visitor sees beyond the
   headline, so it has to earn the space without disclosing anything. */
/* The real folder structure (HANDOVER.md §0) — safe to show in full on
   Welcome (authenticated-only), unlike on the public gate above. */
const DRIVES = [
  ['00', 'RMS Company Hub'], ['01', 'RMS Group'],
  ['02', 'RMS KE Kenya'], ['03', 'RMS TZ Tanzania'], ['04', 'RMS UG Uganda'],
  ['05', 'RMS NG Nigeria'], ['06', 'RMS ZM Zambia'], ['07', 'RMS IN India'],
  ['08', 'RMS CN China'], ['09', 'RMS GP Global Partnerships'],
  ['10', 'RMS MU Mauritius'], ['11', 'RMS US United States'],
] as const

const BRANCHES = [
  '01 Corporate & Governance', '02 Legal', '03 Finance & Accounts',
  '04 People (HR)', '05 Technology', '06 Operations',
]

const GATE_VALUES = [
  {
    title: 'Always know where you stand',
    body: 'Every folder shows what you can do with it, not just whether you can see it.',
  },
  {
    title: 'A named owner, never a black box',
    body: 'Nothing sits in nobody’s territory. Every folder has one accountable owner you can ask.',
  },
  {
    title: 'You move your own files, on your click',
    body: 'No bulk migration is ever forced on you. Nothing moves without your click.',
  },
]

export default function SignInGuide() {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const go = async () => {
    setBusy(true); setError(null)
    try { await signIn() }
    catch (e) {
      const code = (e as { code?: string }).code
      setError(
        code === 'auth/popup-blocked'
          ? 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.'
          : code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request'
            ? 'The sign-in window was closed before you finished. Try again when ready.'
            : 'Sign-in did not complete. Try again, or ask in #rms-help.',
      )
    }
    finally { setBusy(false) }
  }

  return (
    <div className="gate">
      <div className="gate__row">
        <section className="guide__hero">
          <p className="eyebrow">d.light Records Management System</p>
          <h1 className="gate__headline">One home for d.light's records</h1>
          <p className="guide__lead">
            RMS brings every company file, scattered across personal drives, disjointed shared drives, chats
            and email, into one structured, coherent system, with a clear owner for every folder. Predefined
            access levels make sure the right people and teams get the right access, automatically.
          </p>
          <button className="btn btn--primary" onClick={go} disabled={busy}>
            {busy ? 'Waiting for Google sign-in…' : 'Sign in with Google'}
          </button>
          <p className="note" style={{ marginTop: 'var(--s-sm)' }}>
            Use your d.light Google account. A Google window opens; if nothing appears, check it has not
            opened behind this one.
          </p>
          {error && <p className="guide__error" role="alert">{error}</p>}
        </section>

        {/* Real fact, not decoration: RMS doesn't move files off Google, it
            organises what's already there. Worth saying up front, before
            anyone wonders whether signing in means migrating anything. */}
        <div className="gate__drive">
          <img src={driveLogoUrl} alt="Google Drive" />
          <p>All files stay in Google Drive, just organised and ISO-compliant.</p>
        </div>

        {/* Fills the space a structure walkthrough used to occupy here, without
            disclosing any of it pre-login — three true, general claims about
            what the portal does, not what d.light's drives/branches are named. */}
        <aside className="gate__values" aria-label="What changes once you sign in">
          {GATE_VALUES.map((v, i) => (
            <div className="gate__principle" key={v.title}>
              <span className="mono">{String(i + 1).padStart(2, '0')}</span>
              <div>
                <h3>{v.title}</h3>
                <p>{v.body}</p>
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  )
}

/* Shown once, right after a successful sign-in — see App.tsx. Authenticated-only.
   The elevator pitch: what RMS is, why it exists, what it changes for the org.
   Not the structure walkthrough — that's Learn's job (nav, always reachable,
   links the real training docs), and having both here as well as there is
   exactly the duplication a prior version of this screen got flagged for. */
export function Welcome({ onContinue, cta = 'Continue to RMS' }: { onContinue: () => void; cta?: string }) {
  return (
    <div className="welcome">
      <div className="welcome__row">
        <div className="welcome__body">
          <p className="eyebrow">Welcome</p>
          <h1 style={{ marginBottom: 'var(--s-lg)' }}>Why RMS exists</h1>

          <p>
            RMS is d.light's records management system, built on Google Shared Drives to serve as a
            centralised hub for files that used to be scattered across personal drives, chat threads, email
            attachments, and forgotten shared drives. Every folder now has a designated location, a clear
            name, and one accountable owner, with a consistent structure applied across every entity, from
            Kenya to Mauritius.
          </p>
          <p>
            RMS exists because scattered records are a liability, not just an inconvenience. By following the
            ISO 15489 records-management standard, RMS eliminates failure modes like unfindable distributor
            contracts, conflicting policy versions, and lingering access permissions.
          </p>
          <p>
            Day to day, this means less time spent searching for documents, and My access
            shows where your files belong, removing the guesswork. For d.light as a whole, it means records
            survive role changes and staff departures, audits have a single point of reference, and folder
            ownership is clear without an email chain or chat thread.
          </p>

          <button className="btn btn--primary" onClick={onContinue}>{cta}</button>
        </div>

        <aside className="welcome__structure" aria-label="The folder structure">
          <h2>Twelve shared drives</h2>
          <div className="welcome__drives">
            {DRIVES.map(([code, name]) => (
              <div className="welcome__driveitem" key={code}>
                <span className="mono">{code}</span>
                <span>{name}</span>
              </div>
            ))}
          </div>
          <h2>Six branches, inside every drive</h2>
          <div className="chips">
            {BRANCHES.map((b) => <span key={b} className="pill pill--neutral">{b}</span>)}
          </div>
        </aside>
      </div>
    </div>
  )
}
